import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import { keyframes } from '@mui/system';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useMediaStream, useMediaRecorder, useVideoUpload, useDeviceOrientation } from './hooks';
import { CameraFlipButton, RecordingControls, UploadSection } from './components';
import { AspectRatio, RECORDING_CONFIG } from './types';
import { useCountdown } from '@/hooks/useCountdown';

// Re-export types and utilities for backwards compatibility
export type { RecordedVideo } from './types';
// eslint-disable-next-line react-refresh/only-export-components
export { pickMime } from './hooks/useMediaRecorder';

interface RecorderProps {
  maxDuration?: number;
  maxResolution?: number;
  planLimit: number;
}

const Recorder: React.FC<RecorderProps> = ({
  maxDuration = RECORDING_CONFIG.DEFAULT_MAX_DURATION,
  maxResolution,
  planLimit,
}) => {
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const { countdownValue, isCountingDown, runCountdown, cancelCountdown } = useCountdown();
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [safariStreamRequested, setSafariStreamRequested] = useState(false);
  const autoUploadTriggeredRef = useRef(false);
  const restartRequestedRef = useRef(false);
  // Explicit signal that a restart was requested (CONTRACT #7). Without a real
  // state dependency the restart effect below only fired because `useMediaRecorder`
  // returns a fresh object every render, which made `handleStartRecording` a new
  // identity each time — i.e. restart worked by accident, and any memoization of
  // the recorder would silently kill it.
  const [restartTick, setRestartTick] = useState(0);
  const isRestartIntentRef = useRef(false);

  // Device orientation for mobile-optimised layout
  const { isMobile, isPortrait } = useDeviceOrientation();

  // Safari iOS requires getUserMedia() within a direct user gesture (tap/click)
  const isSafariIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const isSafari = /^((?!chrome|android|CriOS|FxiOS|EdgiOS).)*safari/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isSafari && isIOS;
  }, []);

  const effectiveResolution = useMemo(() => {
    const cap = isMobile ? RECORDING_CONFIG.MOBILE_MAX_RESOLUTION : RECORDING_CONFIG.MAX_RECORDING_RESOLUTION;
    if (!maxResolution) return isMobile ? cap : undefined;
    return Math.min(maxResolution, cap);
  }, [maxResolution, isMobile]);

  // Custom hooks
  const mediaStream = useMediaStream();
  const {
    availableCameras,
    cameraWarning,
    clearCameraWarning,
    clearError: clearMediaError,
    cleanupStream,
    error: mediaError,
    isSwitchingCamera,
    preferredFacingMode,
    previewRef,
    rawStream,
    requestStream,
    selectCamera,
    selectedCameraId,
    availableMics,
    micWarning,
    clearMicWarning,
    isSwitchingMic,
    selectMic,
    selectedMicId,
  } = mediaStream;

  const recorder = useMediaRecorder({ maxDuration, resolution: effectiveResolution, isMobile });

  const handleUploadComplete = useCallback(
    (result: { success: boolean; noteId?: string; filePath?: string; error?: string }) => {
      if (result.success) {
        setUploadComplete(true);
      }
    },
    [],
  );

  const uploader = useVideoUpload({
    onUploadComplete: handleUploadComplete,
  });

  // Get the current recorded video
  const currentVideo = recorder.recordedVideo;

  // Format time helper
  const formatTime = useCallback((seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) {
      return '00:00';
    }
    const date = new Date(seconds * 1000);
    try {
      return date.toISOString().substring(14, 19);
    } catch {
      return '00:00';
    }
  }, []);

  // Warn user before leaving the page when a recording exists
  useEffect(() => {
    const hasUnsavedVideo = recorder.isRecording || Boolean(currentVideo);
    if (!hasUnsavedVideo) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [recorder.isRecording, currentVideo]);

  // On mobile, auto-set aspect ratio from device orientation
  useEffect(() => {
    if (!isMobile) return;
    setAspectRatio(isPortrait ? '9:16' : '16:9');
  }, [isMobile, isPortrait]);

  const streamConstraints = useMemo(() => {
    if (!effectiveResolution) {
      return {};
    }

    const isLandscape = aspectRatio === '16:9';
    const maxHeight = effectiveResolution;
    const maxWidth = Math.round((effectiveResolution * (isLandscape ? 16 : 9)) / (isLandscape ? 9 : 16));
    const frameRate = isMobile ? RECORDING_CONFIG.MOBILE_FRAME_RATE : undefined;

    // On mobile portrait, only constrain height to prevent browser from
    // digitally cropping/zooming the front camera to meet a narrow width max
    if (isMobile && !isLandscape) {
      return { maxHeight, frameRate };
    }

    return { maxWidth, maxHeight, frameRate };
  }, [aspectRatio, effectiveResolution, isMobile]);

  const countdownPulse = useMemo(
    () =>
      keyframes`
        0% { transform: scale(1.4); opacity: 0.7; }
        60% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.95); opacity: 1; }
      `,
    [],
  );

  useEffect(() => {
    if (recorder.isRecording) return;
    // Don't re-acquire the camera while a finished recording is being previewed/uploaded —
    // no viewfinder renders on that screen and the webcam light would stay on.
    // Restart still re-acquires: handleRestart resets the recording (clearing recordedVideo)
    // and explicitly awaits requestStream before flagging the restart effect.
    if (recorder.recordedVideo) return;
    if (isSafariIOS) return; // Safari iOS: let user tap button instead (gesture context required)
    void requestStream(streamConstraints);
  }, [recorder.isRecording, recorder.recordedVideo, requestStream, streamConstraints, isSafariIOS]);

  useEffect(() => {
    return () => {
      cleanupStream();
      cancelCountdown();
    };
  }, [cleanupStream, cancelCountdown]);

  // Handlers that coordinate between hooks
  const handleStartRecording = useCallback(async () => {
    if (isCountingDown || recorder.isRecording) {
      return;
    }

    let stream: MediaStream | null = (previewRef.current?.srcObject as MediaStream | null) ?? null;

    if (!stream) {
      stream = await requestStream(streamConstraints);
    }
    if (!stream) {
      return;
    }

    const countdownCompleted = await runCountdown();
    if (!countdownCompleted) {
      return;
    }

    recorder.startRecording(stream, cleanupStream);
  }, [cleanupStream, isCountingDown, previewRef, recorder, requestStream, runCountdown, streamConstraints]);

  const handleReset = useCallback(async () => {
    cancelCountdown();
    recorder.resetRecording();
    uploader.clearUploadError();
    await requestStream(streamConstraints);
  }, [recorder, requestStream, streamConstraints, uploader, cancelCountdown]);

  const handleRestart = useCallback(async () => {
    cancelCountdown();
    recorder.resetRecording();
    uploader.clearUploadError();
    autoUploadTriggeredRef.current = false;
    setUploadComplete(false);
    await requestStream(streamConstraints);
    // Set flag AFTER stream is acquired so the restart effect fires handleStartRecording
    // only when the stream is already in previewRef — prevents a concurrent getUserMedia race
    restartRequestedRef.current = true;
    // Bump the tick so the restart effect is guaranteed to re-run, rather than
    // relying on incidental re-renders.
    setRestartTick((tick) => tick + 1);
  }, [recorder, requestStream, streamConstraints, uploader, cancelCountdown]);

  const handleUpload = useCallback(async () => {
    if (!currentVideo) return;

    if (currentVideo.duration > planLimit) return;

    await uploader.uploadVideo(currentVideo.file, currentVideo.duration);
  }, [currentVideo, planLimit, uploader]);

  // Confirmation dialog handlers for reset/discard
  const handleResetRequest = useCallback(() => {
    if (recorder.isRecording || currentVideo) {
      isRestartIntentRef.current = false;
      setConfirmResetOpen(true);
    } else {
      void handleReset();
    }
  }, [recorder.isRecording, currentVideo, handleReset]);

  const handleRestartRequest = useCallback(() => {
    if (recorder.isRecording || recorder.isPaused) {
      isRestartIntentRef.current = true;
      setConfirmResetOpen(true);
    } else {
      void handleRestart();
    }
  }, [recorder.isRecording, recorder.isPaused, handleRestart]);

  const handleConfirmReset = useCallback(() => {
    setConfirmResetOpen(false);
    autoUploadTriggeredRef.current = false;
    setUploadComplete(false);
    if (isRestartIntentRef.current) {
      void handleRestart();
    } else {
      void handleReset();
    }
  }, [handleReset, handleRestart]);

  const handleCancelReset = useCallback(() => {
    setConfirmResetOpen(false);
  }, []);

  // Auto-upload when recording completes
  useEffect(() => {
    if (currentVideo && !autoUploadTriggeredRef.current && !uploader.isUploading && !uploadComplete) {
      if (currentVideo.duration <= planLimit) {
        autoUploadTriggeredRef.current = true;
        void uploader.uploadVideo(currentVideo.file, currentVideo.duration);
      }
    }
    if (!currentVideo) {
      autoUploadTriggeredRef.current = false;
      setUploadComplete(false);
    }
  }, [currentVideo, planLimit, uploader.isUploading, uploader.uploadVideo, uploadComplete]);

  // After restart reset completes, trigger countdown + start
  useEffect(() => {
    if (restartRequestedRef.current && !recorder.isRecording && !isCountingDown) {
      restartRequestedRef.current = false;
      void handleStartRecording();
    }
  }, [restartTick, recorder.isRecording, isCountingDown, handleStartRecording]);

  const handleCameraSelect = useCallback(
    async (deviceId: string) => {
      if (recorder.isRecording) {
        clearMediaError();
        return;
      }
      await selectCamera(deviceId, streamConstraints);
    },
    [clearMediaError, recorder.isRecording, selectCamera, streamConstraints],
  );

  const handleMicSelect = useCallback(
    async (deviceId: string) => {
      if (recorder.isRecording) {
        clearMediaError();
        return;
      }
      await selectMic(deviceId, streamConstraints);
    },
    [clearMediaError, recorder.isRecording, selectMic, streamConstraints],
  );

  // Mobile: flip between front and back camera
  const handleCameraFlip = useCallback(async () => {
    if (recorder.isRecording) return;
    const newMode = preferredFacingMode === 'user' ? 'environment' : 'user';
    // Preserve current mic selection when flipping camera
    const currentMicId = rawStream?.getAudioTracks()[0]?.getSettings().deviceId;
    await requestStream({
      ...streamConstraints,
      facing: newMode,
      ...(currentMicId ? { audioDeviceId: currentMicId } : {}),
    });
  }, [preferredFacingMode, rawStream, recorder.isRecording, requestStream, streamConstraints]);

  // Safari iOS: request camera within a user gesture context
  const handleSafariEnableCamera = useCallback(async () => {
    setSafariStreamRequested(true);
    await requestStream(streamConstraints);
  }, [requestStream, streamConstraints]);

  const hasRecording = Boolean(currentVideo);
  const hasMultipleCameras = availableCameras.length > 1;

  // ---------- Countdown Overlay (shared between mobile & desktop) ----------
  const countdownOverlay = countdownValue && (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 5,
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontWeight: 700,
          color: countdownValue === 'Go!' ? '#4ade80' : '#ffffff',
          textShadow: '0 0 40px rgba(255,255,255,0.5), 0 0 80px rgba(43,159,255,0.3)',
          fontSize: {
            xs: countdownValue === 'Go!' ? '70px' : '80px',
            sm: countdownValue === 'Go!' ? '100px' : '120px',
            md: countdownValue === 'Go!' ? '160px' : '200px',
          },
          animation: `${countdownPulse} 0.5s ease-out`,
        }}
      >
        {countdownValue}
      </Typography>
    </Box>
  );

  // Mic is active when the stream has at least one live, enabled audio track
  const isMicActive = rawStream?.getAudioTracks().some((t) => t.readyState === 'live' && t.enabled) ?? false;

  // Camera is active when the stream has at least one live, enabled video track
  const isCameraActive = rawStream?.getVideoTracks().some((t) => t.readyState === 'live' && t.enabled) ?? false;

  // ---------- Safari iOS camera prompt overlay ----------
  const showSafariCameraPrompt = isSafariIOS && !safariStreamRequested && !rawStream && !recorder.isRecording;

  const safariCameraPrompt = showSafariCameraPrompt && (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 5,
        gap: 2,
      }}
    >
      <Button
        variant="contained"
        onClick={handleSafariEnableCamera}
        startIcon={<VideocamIcon />}
        sx={{
          borderRadius: 999,
          px: { xs: 2.5, sm: 4 },
          py: { xs: 1, sm: 1.5 },
          fontSize: { xs: '0.85rem', sm: '1.1rem' },
          fontWeight: 600,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          background: 'linear-gradient(135deg, #2b9fff 0%, #1976d2 100%)',
          boxShadow: '0 4px 20px rgba(43,159,255,0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          },
        }}
      >
        Enable Camera
      </Button>
    </Box>
  );

  // ---------- Recording indicator badge (shared) ----------
  const recordingBadge = recorder.isRecording && (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: 'rgba(0,0,0,0.6)',
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
      }}
    >
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: recorder.isPaused ? 'warning.main' : 'error.main',
        }}
      />
      <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 600 }}>
        {recorder.isPaused ? 'Paused' : 'Recording'}
      </Typography>
    </Box>
  );

  // ==================== MOBILE FULLSCREEN LAYOUT ====================
  if (isMobile && !hasRecording) {
    return (
      <Stack spacing={0} sx={{ width: '100%' }}>
        {/* Camera viewfinder — sized to fit within the visible area */}
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'black',
            borderRadius: 2,
            overflow: 'hidden',
            mx: 'auto',
            aspectRatio: aspectRatio === '9:16' ? '9/16' : '16/9',
            ...(aspectRatio === '9:16'
              ? {
                  // Portrait: width = smaller of (parent width) or (viewport-height-derived width).
                  // 0.5625 = 9/16, so this is the width for a 9:16 box that fits the viewport height.
                  width: 'min(100%, calc((100dvh - 160px) * 0.5625))',
                  '@supports not (height: 1dvh)': {
                    width: 'min(100%, calc((100vh - 160px) * 0.5625))',
                  },
                }
              : {
                  width: '100%',
                  maxHeight: '70dvh',
                  '@supports not (height: 1dvh)': {
                    maxHeight: '70vh',
                  },
                }),
          }}
        >
          <video
            ref={previewRef}
            muted
            playsInline
            autoPlay
            style={
              aspectRatio === '16:9'
                ? {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }
                : {
                    position: 'absolute',
                    height: '100%',
                    width: 'auto',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }
            }
          />

          {recordingBadge}
          {countdownOverlay}
          {safariCameraPrompt}

          {/* Camera flip button — hidden during recording & countdown */}
          {hasMultipleCameras && !recorder.isRecording && !isCountingDown && (
            <CameraFlipButton onFlip={handleCameraFlip} isSwitching={isSwitchingCamera} />
          )}
        </Box>

        {/* Controls toolbar — below the viewfinder */}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <RecordingControls
            isRecording={recorder.isRecording}
            isPaused={recorder.isPaused}
            elapsed={recorder.elapsed}
            maxDuration={maxDuration}
            aspectRatio={aspectRatio}
            onStart={handleStartRecording}
            onPause={recorder.pauseRecording}
            onResume={recorder.resumeRecording}
            onStop={recorder.stopRecording}
            onReset={handleResetRequest}
            onRestart={handleRestartRequest}
            onAspectRatioChange={setAspectRatio}
            disabled={isCountingDown}
            mobile
            cameras={availableCameras}
            selectedCameraId={selectedCameraId}
            onCameraSelect={handleCameraSelect}
            isSwitchingCamera={isSwitchingCamera}
            isCameraActive={isCameraActive}
            microphones={availableMics}
            selectedMicId={selectedMicId}
            onMicSelect={handleMicSelect}
            isSwitchingMic={isSwitchingMic}
            isMicActive={isMicActive}
          />
        </Box>

        {/* Camera warning — shown below toolbar on mobile */}
        {cameraWarning && (
          <Box sx={{ px: 1 }}>
            <Alert severity="warning" onClose={clearCameraWarning} sx={{ mx: 1 }}>
              {cameraWarning}
            </Alert>
          </Box>
        )}

        {/* Mic warning — shown below toolbar on mobile */}
        {micWarning && (
          <Box sx={{ px: 1 }}>
            <Alert severity="warning" onClose={clearMicWarning} sx={{ mx: 1 }}>
              {micWarning}
            </Alert>
          </Box>
        )}

        {/* Error displays */}
        {recorder.error && (
          <Alert severity="error" onClose={recorder.clearError} sx={{ mx: 1, mt: 1 }}>
            {recorder.error}
          </Alert>
        )}
        {mediaError && (
          <Alert severity="error" onClose={clearMediaError} sx={{ mx: 1, mt: 1 }}>
            {mediaError}
          </Alert>
        )}

        {/* Reset confirmation dialog */}
        <Dialog open={confirmResetOpen} onClose={handleCancelReset}>
          <DialogTitle>Discard Recording?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {recorder.isRecording
                ? 'You have an active recording in progress. Are you sure you want to discard it and start over?'
                : 'Are you sure you want to discard this video and start over?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelReset}>Cancel</Button>
            <Button onClick={handleConfirmReset} color="error">
              Discard
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  // ==================== DESKTOP / POST-RECORDING LAYOUT ====================
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={3}>
          {!hasRecording && (
            <>
              {/* Outer container: always 16:9 so height stays consistent */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16/9',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {/* Inner visible area: 16:9 or 9:16 clip window */}
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    aspectRatio: aspectRatio === '16:9' ? '16/9' : '9/16',
                    overflow: 'hidden',
                  }}
                >
                  <video
                    ref={previewRef}
                    muted
                    playsInline
                    autoPlay
                    style={
                      aspectRatio === '16:9'
                        ? {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }
                        : {
                            position: 'absolute',
                            height: '100%',
                            width: 'auto',
                            left: '50%',
                            transform: 'translateX(-50%)',
                          }
                    }
                  />
                  {recordingBadge}
                  {countdownOverlay}
                  {safariCameraPrompt}
                </Box>
              </Box>

              <RecordingControls
                isRecording={recorder.isRecording}
                isPaused={recorder.isPaused}
                elapsed={recorder.elapsed}
                maxDuration={maxDuration}
                aspectRatio={aspectRatio}
                onStart={handleStartRecording}
                onPause={recorder.pauseRecording}
                onResume={recorder.resumeRecording}
                onStop={recorder.stopRecording}
                onReset={handleResetRequest}
                onRestart={handleRestartRequest}
                onAspectRatioChange={setAspectRatio}
                disabled={isCountingDown}
                cameras={availableCameras}
                selectedCameraId={selectedCameraId}
                onCameraSelect={handleCameraSelect}
                isSwitchingCamera={isSwitchingCamera}
                isCameraActive={isCameraActive}
                microphones={availableMics}
                selectedMicId={selectedMicId}
                onMicSelect={handleMicSelect}
                isSwitchingMic={isSwitchingMic}
                isMicActive={isMicActive}
              />
            </>
          )}

          {currentVideo && (
            <>
              {uploadComplete ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 6, sm: 8 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Upload Complete!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Redirecting to your note...
                  </Typography>
                </Box>
              ) : uploader.isUploading ? (
                <UploadSection
                  isUploading={uploader.isUploading}
                  progress={uploader.uploadProgress}
                  error={uploader.uploadError}
                  selectedDuration={currentVideo.duration}
                  maxDuration={planLimit}
                  onUpload={handleUpload}
                  onReset={handleResetRequest}
                  onClearError={uploader.clearUploadError}
                  formatTime={formatTime}
                  recoveryFile={{ file: currentVideo.file, name: currentVideo.name }}
                />
              ) : (
                <>
                  {/* Video Preview - shown when auto-upload cannot proceed (e.g., exceeds plan limit) */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Preview Your Video
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Playback preview
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: 'black',
                        mb: 2,
                        ...(aspectRatio === '9:16' && {
                          maxWidth: 360,
                          mx: 'auto',
                        }),
                      }}
                    >
                      <video
                        src={currentVideo.url}
                        controls
                        playsInline
                        style={{
                          width: '100%',
                          aspectRatio: aspectRatio === '16:9' ? '16/9' : '9/16',
                          backgroundColor: 'black',
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Duration: {formatTime(currentVideo.duration)}
                    </Typography>
                  </Box>

                  <UploadSection
                    isUploading={uploader.isUploading}
                    progress={uploader.uploadProgress}
                    error={uploader.uploadError}
                    selectedDuration={currentVideo.duration}
                    maxDuration={planLimit}
                    onUpload={handleUpload}
                    onReset={handleResetRequest}
                    onClearError={uploader.clearUploadError}
                    formatTime={formatTime}
                    recoveryFile={{ file: currentVideo.file, name: currentVideo.name }}
                  />
                </>
              )}
            </>
          )}

          {/* Error displays */}
          {recorder.error && (
            <Alert severity="error" onClose={recorder.clearError}>
              {recorder.error}
            </Alert>
          )}

          {mediaError && (
            <Alert severity="error" onClose={clearMediaError}>
              {mediaError}
            </Alert>
          )}
        </Stack>
      </CardContent>

      {/* Reset confirmation dialog */}
      <Dialog open={confirmResetOpen} onClose={handleCancelReset}>
        <DialogTitle>Discard Recording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {recorder.isRecording
              ? 'You have an active recording in progress. Are you sure you want to discard it and start over?'
              : 'Are you sure you want to discard this video and start over?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="error">
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default Recorder;
